import { Controller, useFieldArray, useWatch } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { PlusIcon } from '@gto/icons-react/outline/plus'
import { TrashIcon } from '@gto/icons-react/outline/trash'
import { Button } from '@gto/ui/button'
import { IconButton } from '@gto/ui/icon-button'
import { Typography } from '@gto/ui/typography'

import { MAX_SOCIAL_LINKS, PLATFORM_LABEL_IDS, SOCIAL_PLATFORMS } from '@/domain/social-links'

import { fieldErrorText, SelectField, TextField } from './form'
import type { SocialLinksForm } from './use-social-links-form'

/**
 * Repeatable "Social links" editor: one row per link (platform Select, a
 * custom-only Label, and a URL) plus an "Add link" button capped at
 * {@link MAX_SOCIAL_LINKS}. Driven by `useFieldArray` over the single
 * `socialLinksFormSchema`; `<SocialLinksSummary>` renders the read view and
 * `useSocialLinksForm` wires the form. The server 422 stays the authority — its
 * per-row errors surface via `setError`, everything else through the caller's
 * inline alert.
 */

type SocialLinkRowProps = {
  form: SocialLinksForm
  index: number
  disabled: boolean
  onRemove: () => void
}

const SocialLinkRow = ({ form, index, disabled, onRemove }: SocialLinkRowProps) => {
  const { formatMessage } = useIntl()
  const { control } = form

  // `useWatch` (not `form.watch`) so the row re-subscribes and re-renders on its
  // own when its platform changes — the label field must appear/vanish as the
  // partner switches to/from `custom`. Under the React Compiler (active in the
  // browser build) a parent re-render would not refresh a plain `watch()` read.
  const platform = useWatch({ control, name: `links.${index}.platform` })
  const isCustom = platform === 'custom'

  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-divider p-3"
      data-tst={`social-link-row-${index}`}
    >
      <div className="flex items-start gap-2">
        <Controller
          control={control}
          name={`links.${index}.platform`}
          render={({ field, fieldState }) => (
            <SelectField
              className="flex-1"
              disabled={disabled}
              error={fieldErrorText(formatMessage, fieldState.error, 'socialLinks.')}
              label={formatMessage({ id: 'socialLinks.platform' })}
              name={field.name}
              triggerRef={field.ref}
              value={field.value}
              options={SOCIAL_PLATFORMS.map((option) => ({
                value: option,
                label: formatMessage({ id: PLATFORM_LABEL_IDS[option] }),
              }))}
              onBlur={field.onBlur}
              onValueChange={field.onChange}
            />
          )}
        />
        <IconButton
          aria-label={formatMessage({ id: 'socialLinks.remove' })}
          data-tst={`social-link-remove-${index}`}
          disabled={disabled}
          size="small"
          onClick={onRemove}
        >
          <TrashIcon
            height={18}
            width={18}
          />
        </IconButton>
      </div>

      {isCustom && (
        <Controller
          control={control}
          name={`links.${index}.label`}
          render={({ field, fieldState }) => (
            <TextField
              ref={field.ref}
              disabled={disabled}
              error={fieldErrorText(formatMessage, fieldState.error, 'socialLinks.')}
              label={formatMessage({ id: 'socialLinks.label' })}
              name={field.name}
              value={field.value}
              onBlur={field.onBlur}
              onChange={field.onChange}
            />
          )}
        />
      )}

      <Controller
        control={control}
        name={`links.${index}.url`}
        render={({ field, fieldState }) => (
          <TextField
            ref={field.ref}
            disabled={disabled}
            error={fieldErrorText(formatMessage, fieldState.error, 'socialLinks.')}
            label={formatMessage({ id: 'socialLinks.url' })}
            name={field.name}
            placeholder="https://"
            value={field.value}
            onBlur={field.onBlur}
            onChange={field.onChange}
          />
        )}
      />
    </div>
  )
}

type SocialLinksFieldsProps = {
  form: SocialLinksForm
  disabled?: boolean
}

export const SocialLinksFields = ({ form, disabled = false }: SocialLinksFieldsProps) => {
  const { control } = form
  const { fields, append, remove } = useFieldArray({ control, name: 'links' })

  return (
    <div className="flex flex-col gap-3">
      <Typography
        className="font-medium"
        variant="body2"
      >
        <FormattedMessage id="profile.socialLinks" />
      </Typography>

      {fields.length === 0 && (
        <Typography
          color="text.secondary"
          variant="body2"
        >
          <FormattedMessage id="socialLinks.empty" />
        </Typography>
      )}

      {fields.map((field, index) => (
        <SocialLinkRow
          key={field.id}
          disabled={disabled}
          form={form}
          index={index}
          onRemove={() => remove(index)}
        />
      ))}

      <div>
        <Button
          data-tst="social-link-add"
          disabled={disabled || fields.length >= MAX_SOCIAL_LINKS}
          size="small"
          variant="outlined"
          startIcon={
            <PlusIcon
              height={16}
              width={16}
            />
          }
          onClick={() => append({ platform: '', label: '', url: '' })}
        >
          <FormattedMessage id="socialLinks.add" />
        </Button>
      </div>
    </div>
  )
}
